import { Box, Typography } from '@mui/material';
import { FormattedMessage } from 'react-intl';

import { SiteLocale } from 'constants/i18n.constants';
import { useLocale } from 'hooks/useLocale';
import { DEFAULT_CURRENCY, type MoneyInfo, toMoneyInfo } from 'restful/tickets';

import FormattedMoneyInfo from 'components/@tickets/FormattedMoneyInfo';

interface Props {
  originalPrice?: MoneyInfo | number;
  discountedPrice?: MoneyInfo | number;
  color?: string;
}

const TicketPrice = ({
  originalPrice,
  discountedPrice,
  color = 'text.primary',
}: Props) => {
  const { locale } = useLocale();
  const originalMoneyInfo = toMoneyInfo(originalPrice);
  const discountedMoneyInfo = toMoneyInfo(discountedPrice);
  const currency =
    originalMoneyInfo?.currency ??
    discountedMoneyInfo?.currency ??
    DEFAULT_CURRENCY;

  const isDiscounted =
    !!originalMoneyInfo?.amount &&
    originalMoneyInfo.amount !== discountedMoneyInfo?.amount;

  // Don't render if price is 0
  const currentPrice = isDiscounted
    ? originalMoneyInfo?.amount
    : discountedMoneyInfo?.amount;

  if (currentPrice === 0) {
    return null;
  }

  return (
    <Box
      display="flex"
      flexDirection={locale === SiteLocale.AR ? 'row-reverse' : 'row'}
      alignSelf="flex-start"
      gap={0.5}
    >
      <Typography variant="extraSmall" fontWeight={500} mr="2px" color={color}>
        <FormattedMessage id={`currency.${currency.toLowerCase()}.label`} />
      </Typography>
      {isDiscounted && (
        <Typography
          variant="extraSmall"
          sx={{
            fontWeight: 500,
            textDecoration: 'line-through',
            color,
            mr: 0.5,
          }}
        >
          <FormattedMoneyInfo price={originalPrice} style="decimal" />
        </Typography>
      )}
      <Typography
        variant="extraSmall"
        fontWeight={500}
        color={isDiscounted ? 'global01.400' : color}
      >
        <FormattedMoneyInfo price={discountedPrice} style="decimal" />
      </Typography>
    </Box>
  );
};

export default TicketPrice;
